import React, { useState } from 'react';
import EmptyState from '../ui/EmptyState';
import SectionEditor from './SectionEditor';
import AddSectionButton from './AddSectionButton';
import SidebarCard from './SidebarCard';
import { getSectionPreview, typeLabel } from './sectionTypes';

/**
 * Body sections in page order with "+" insert points between them, plus a
 * collapsed fallback card for sections that exist but are not in the body.
 */
export default function SectionList({
  isNewPage,
  body,
  sections,
  expandedIds,
  onToggle,
  onExpandAll,
  onCollapseAll,
  onCreate,
  onMove,
  onDelete,
  onSave,
  onDirtyChange,
  onAddOrphan,
  busy,
  savedVersions,
}) {
  const [orphansOpen, setOrphansOpen] = useState(false);

  if (isNewPage) {
    return (
      <section className="card" aria-labelledby="sections-title">
        <h3 className="card-title" id="sections-title">Page sections</h3>
        <p className="hint" style={{ margin: 0, fontSize: 13, color: 'var(--color-muted)' }}>
          Create the page first (Save or Ctrl+S); sections can be added straight after.
        </p>
      </section>
    );
  }

  const byId = new Map((sections || []).map((s) => [s.id, s]));
  const ordered = (body || []).map((id) => byId.get(id)).filter(Boolean);
  const bodySet = new Set(body || []);
  const orphans = (sections || []).filter((s) => !bodySet.has(s.id));

  return (
    <>
      <section aria-labelledby="sections-title">
        <div className="card-header" style={{ marginBottom: 8 }}>
          <h3 className="card-title" id="sections-title" style={{ margin: 0 }}>
            Page sections <span style={{ fontWeight: 400 }}>({ordered.length})</span>
          </h3>
          {ordered.length > 1 && (
            <span style={{ display: 'inline-flex', gap: 4 }}>
              <button type="button" className="btn btn-ghost btn-sm" onClick={onExpandAll}>Expand all</button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={onCollapseAll}>Collapse all</button>
            </span>
          )}
        </div>

        {ordered.length === 0 ? (
          <EmptyState
            title="No sections yet"
            message="Sections are the body of the page: Markdown, images, galleries, quotes, embeds and more."
            action={<AddSectionButton position={0} onAdd={onCreate} busy={busy} prominent />}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {ordered.map((section, index) => (
              <React.Fragment key={section.id}>
                <AddSectionButton position={index} onAdd={onCreate} busy={busy} />
                <SectionEditor
                  section={section}
                  index={index}
                  count={ordered.length}
                  expanded={expandedIds.has(section.id)}
                  onToggle={() => onToggle(section.id)}
                  onMove={onMove}
                  onDelete={onDelete}
                  onSave={onSave}
                  onDirtyChange={onDirtyChange}
                  busy={busy}
                  savedVersion={savedVersions[section.id] || 0}
                />
              </React.Fragment>
            ))}
            <AddSectionButton position={ordered.length} onAdd={onCreate} busy={busy} prominent />
          </div>
        )}
      </section>

      {orphans.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <SidebarCard id="orphans" title={`Orphaned sections (${orphans.length})`} open={orphansOpen} onToggle={() => setOrphansOpen((o) => !o)}>
            <p className="hint" style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--color-muted)' }}>
              Created earlier but not placed in the page body. Add them to the page or delete them.
            </p>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {orphans.map((s) => (
                <li key={s.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                  <span className="chip" style={{ padding: '1px 8px', fontSize: 11 }}>{typeLabel(s.type)}</span>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getSectionPreview(s)}</span>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => onAddOrphan(s.id)} disabled={busy}>Add to page</button>
                  <button type="button" className="btn btn-ghost btn-sm btn-danger-text" onClick={() => onDelete(s.id)} disabled={busy} aria-label={`Delete orphaned ${typeLabel(s.type)} section`}>✕</button>
                </li>
              ))}
            </ul>
          </SidebarCard>
        </div>
      )}
    </>
  );
}
